import { FC, useCallback, useContext, useState } from "react";
import type { MouseEvent } from "react";
import { useNavigate } from "react-router";

import { Button, Buttons, Message, Messages, useAlert } from "capitalrx-components-2.0/lib/general";
import { useResponsive } from "capitalrx-components-2.0/lib/layout";
import { STANDARD_RESPONSIVE_LAYOUT } from "@/constants";
import { Body, Semibold } from "capitalrx-components-2.0/lib/typography";

import { createRenewalCodeAPI } from "@/api";
import { AuthenticationContext } from "@/context";
import { getDaysUntilExpiration, getThemeURL, isRenewalWindowOpen } from "@/utils";

interface PACaseRenewalBannerProps {
  caseId: number;
  expirationDate?: string;
  renewalWindowDate?: string;
  hasRenewalCode?: boolean;
  status?: string;
  showRenewalInstructions?: boolean;
}

export const PACaseRenewalBanner: FC<PACaseRenewalBannerProps> = ({
  caseId,
  expirationDate,
  renewalWindowDate,
  hasRenewalCode = false,
  status,
  showRenewalInstructions = false,
}) => {
  const navigate = useNavigate();
  const showAlert = useAlert();
  const { getAuthToken } = useContext(AuthenticationContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { Layout } = useResponsive(STANDARD_RESPONSIVE_LAYOUT);

  const renewCase = useCallback(async (event?: MouseEvent<HTMLButtonElement>) => {
    event?.stopPropagation();
    setIsLoading(true);

    try {
      const renewal = await createRenewalCodeAPI(
        { case_id: caseId },
        getAuthToken(),
      );
      const encodedParams = new URLSearchParams();
      encodedParams.append("prescriber_npi", renewal.npi);
      encodedParams.append("renewal_code", renewal.code);

      navigate({
        pathname: getThemeURL("PA_CREATE"),
        search: encodedParams.toString(),
      });
    } catch (error) {
      setIsLoading(false);
      showAlert({ type: "error", style: "banner", message: "This case cannot be renewed right now" });
    }
  }, [caseId, getAuthToken, navigate, showAlert]);

  if (status === "reopen" || hasRenewalCode || !isRenewalWindowOpen(renewalWindowDate, expirationDate)) {
    return null;
  }

  const daysUntilExpiration = getDaysUntilExpiration(expirationDate);
  const renewButton = (
    <Button
      appearance={Buttons.Functional.Tertiary.Compact.Unpadded}
      onClick={renewCase}
      disabled={isLoading}
      testId="test__pa_case_renew_now"
    >
      Renew Now
    </Button>
  );

  if (!showRenewalInstructions) {
    return (
      <Message
        style={{ position: "relative", zIndex: 9999 }}
        appearance={Messages.Warning}
        visible
        testId="test__pa_case_renewal_banner"
      >
        <Layout spacing="small">
          <Body>
            This authorization is expiring in <Semibold>{daysUntilExpiration} days</Semibold>
          </Body>
          <Layout.Spacer />
          {renewButton}
        </Layout>
      </Message>
    );
  }

  return (
    <Message
      style={{ position: "relative", zIndex: 9999 }}
      appearance={Messages.Warning}
      visible
      actionContent={renewButton}
      testId="test__pa_case_renewal_banner"
    >
      <Body>
        This authorization is expiring in <Semibold>{daysUntilExpiration} days</Semibold>. Renew now to avoid interruptions in treatment.
      </Body>
    </Message>
  );
};

export default PACaseRenewalBanner;
